import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { Button, Flex, Text, useWalletModal, useMatchBreakpoints, useIsMounted } from 'toonswap-uikit'
import { useWeb3React } from '@web3-react/core'
import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import useAuth from 'hooks/useAuth'
import useDailyCheckinClaimable from 'hooks/useDailyCheckinClaimable'
// import { usePriceBowUsd } from 'state/hooks'
import PhishingWarningBanner from 'components/PhishingWarningBanner'
import usePhishingBanner from 'hooks/usePhishingBanner'
import { SHOW_PHISHING_BANNER } from 'config'
import config from './config'
import { postFaucet } from 'hooks/faucetApi'
import { useToast } from 'state/hooks'

const MENU_HEIGHT = 100
const MENU_HEIGHT_MOBILE = 108
const TOP_BANNER_HEIGHT = 70
const TOP_BANNER_HEIGHT_MOBILE = 100
const HEADER_TOP_GAP_PX = 36
const MAIN_CONTENT_MAX_WIDTH_PX = 1270

// Consistent breakpoints (align with codebase: 320, 375, 768, 1024)
const BP_XS = '320px'
const BP_SM = '375px'
const BP_MD = '768px'
const BP_LG = '1024px'

// Menu bar theme (from design)
const MENU_BAR_BG = '#374244'
const MENU_BAR_BORDER = '#1D2425'
const MENU_NAV_INSET_BG = '#475355'
const MENU_NAV_INSET_BORDER = '#2A3335'
const MENU_ACCENT = '#FE7B2A'
const MENU_TEXT = '#FFFFFF'

// Paths that show Home tab as selected (swap, liquidity/pool, cross-swap, bridge, zap, find, add/remove)
const isHomeActivePath = (pathname: string) =>
  pathname === '/swap' ||
  pathname.startsWith('/pool') ||
  pathname.startsWith('/cross-swap') ||
  pathname.startsWith('/bridge') ||
  pathname.startsWith('/zap') ||
  pathname.startsWith('/find') ||
  pathname.startsWith('/add') ||
  pathname.startsWith('/remove')

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`

const FixedContainer = styled.div<{ showMenu: boolean; height: number }>`
  position: fixed;
  top: ${({ showMenu, height }) => (showMenu ? HEADER_TOP_GAP_PX : -(height + HEADER_TOP_GAP_PX))}px;
  left: 0;
  right: 0;
  transition: top 0.2s;
  height: ${({ height }) => height}px;
  width: 100%;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const HeaderInner = styled.div`
  width: 100%;
  max-width: ${MAIN_CONTENT_MAX_WIDTH_PX}px;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 8px;
  @media (min-width: ${BP_SM}) {
    padding: 0 12px;
  }
  @media (min-width: ${BP_MD}) {
    padding: 0 16px;
  }
  @media (min-width: ${BP_LG}) {
    padding: 0;
  }
`

const TopBannerContainer = styled.div<{ height: number }>`
  height: ${({ height }) => height}px;
  min-height: ${({ height }) => height}px;
  max-height: ${({ height }) => height}px;
  width: 100%;
`

const StyledNav = styled.nav`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  min-height: ${MENU_HEIGHT_MOBILE}px;
  background-color: ${MENU_BAR_BG};
  border-radius: 12px;
  border: 2px solid ${MENU_BAR_BORDER};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 8px 10px 10px;
  gap: 10px;
  @media (min-width: ${BP_SM}) {
    padding: 10px 12px 12px;
    border-radius: 14px;
    gap: 12px;
  }
  @media (min-width: ${BP_LG}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    min-height: ${MENU_HEIGHT}px;
    height: ${MENU_HEIGHT}px;
    border-radius: 27px;
    border-width: 3px;
    padding: 0 24px;
    gap: 8px;
  }
`

const NavFirstRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 40px;
  gap: 8px;
  @media (min-width: ${BP_LG}) {
    min-height: 0;
    flex: 1;
    max-width: 100%;
  }
`

const NavIconsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  @media (min-width: ${BP_LG}) {
    display: none;
  }
  /* Consistent 40x40 touch targets, centred content */
  & > a,
  & > div {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 10px !important;
    background-color: ${MENU_NAV_INSET_BG} !important;
    border: 2px solid ${MENU_NAV_INSET_BORDER} !important;
  }
  & > a img {
    width: 20px !important;
    height: 20px !important;
  }
  & > div button {
    width: 100% !important;
    height: 100% !important;
    min-width: 40px !important;
    min-height: 40px !important;
    padding: 0 !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 10px !important;
    background: ${MENU_NAV_INSET_BG} !important;
    border: 2px solid ${MENU_NAV_INSET_BORDER} !important;
    color: ${MENU_TEXT} !important;
  }
  & > div button * {
    margin: 0 !important;
    padding: 0 !important;
    line-height: 0 !important;
  }
`

const ConnectRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: stretch;
  align-items: center;
  @media (min-width: ${BP_LG}) {
    display: none;
  }
`

const LogoLink = styled.a`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  img {
    height: 28px;
    width: auto;
  }
  @media (min-width: ${BP_SM}) {
    img {
      height: 34px;
    }
  }
  @media (min-width: ${BP_MD}) {
    img {
      height: 43px;
    }
  }
`

const NavLinksWrapper = styled.div`
  display: none;
  align-items: center;
  background-color: ${MENU_NAV_INSET_BG};
  border-radius: 16px;
  border: 2px solid ${MENU_NAV_INSET_BORDER};
  padding: 4px 8px;
  gap: 4px;
  flex: 1;
  justify-content: center;
  max-width: fit-content;
  margin: 0 auto;
  @media (min-width: ${BP_LG}) {
    display: flex;
  }
`

const NavLinks = styled(Flex)`
  display: flex;
  align-items: center;
  gap: 10px;
`

const NavLinkItem = styled.div<{ isActive?: boolean }>`
  a,
  .nav-link-inner {
    display: flex;
    align-items: center;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${({ isActive }) => (isActive ? MENU_TEXT : MENU_TEXT)};
    background-color: ${({ isActive }) => (isActive ? MENU_ACCENT : 'transparent')};
    text-decoration: none;
    white-space: nowrap;
    transition: background-color 0.2s;
    &:hover {
      color: ${MENU_TEXT};
      background-color: ${({ isActive }) => (isActive ? MENU_ACCENT : 'rgba(255,255,255,0.1)')};
    }
  }
`

const LabelWithDot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const ClaimableDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff3b30;
  flex-shrink: 0;
`

// Social links (header)
const TELEGRAM_URL = 'https://t.me/toonswapsupra'
const MEDIUM_URL = 'https://x.com/toonswapsupra'

const SocialIconLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 2px solid #2b3335;
  background-color: ${MENU_NAV_INSET_BG};
  color: ${MENU_TEXT};
  transition: opacity 0.2s;
  flex-shrink: 0;
  &:hover {
    opacity: 0.9;
    color: ${MENU_TEXT};
  }
  svg,
  img {
    width: 14px;
    height: 14px;
  }
  @media (min-width: ${BP_SM}) {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    svg,
    img {
      width: 16px;
      height: 16px;
    }
  }
  @media (min-width: ${BP_MD}) {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    svg,
    img {
      width: 18px;
      height: 18px;
    }
  }
`

const MoreDropdownContent = styled.div`
  background-color: ${MENU_NAV_INSET_BG};
  border: 2px solid ${MENU_NAV_INSET_BORDER};
  border-radius: 14px;
  margin: -6px;
  padding: 2px;
  min-width: 140px;
  & a,
  & .sub-item {
    color: ${MENU_TEXT};
  }
  & a:hover {
    opacity: 0.9;
  }
`

const ClickDropdownWrap = styled.div`
  position: relative;
`

const ClickDropdownMenu = styled(MoreDropdownContent)`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
`

const RightSection = styled(Flex)`
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  min-width: 0;
  display: none;
  @media (min-width: ${BP_LG}) {
    display: flex;
    gap: 8px;
  }
  & .menu-price {
    color: ${MENU_TEXT} !important;
  }
`

const SuccessButton = styled.button`
  background-color: #00c853 !important;
  color: #ffffff;
  min-height: 40px;
  padding: 0 16px;
  width: 100%;
  border: 3px solid #2b3335;
  border-radius: 12px;
  font-weight: 600;
`

const StyledButton = styled(Button)`
  min-height: 34px !important;
  padding: 0 10px !important;
  background: ${MENU_ACCENT} !important;
  color: ${MENU_TEXT} !important;
  border: 2px solid #2b3335 !important;
  border-radius: 10px !important;
  font-weight: 600;
  font-size: 12px !important;
  white-space: nowrap;
  &:hover:not(:disabled) {
    opacity: 0.9;
    background: ${MENU_ACCENT} !important;
    color: ${MENU_TEXT} !important;
  }
  &:disabled {
    opacity: 0.5;
  }
  @media (min-width: ${BP_SM}) {
    min-height: 36px !important;
    padding: 0 12px !important;
    border-radius: 10px !important;
  }
  @media (min-width: ${BP_MD}) {
    min-height: 40px !important;
    padding: 0 16px !important;
    border: 3px solid #2b3335 !important;
    border-radius: 12px !important;
    font-size: 14px !important;
  }
`

const BodyWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  max-width: ${MAIN_CONTENT_MAX_WIDTH_PX}px;
  margin-left: auto;
  margin-right: auto;
`

const Inner = styled.div<{ topOffset: number }>`
  flex-grow: 1;
  margin-top: ${({ topOffset }) => topOffset}px;
  transition: margin-top 0.2s;
  max-width: 100%;
  width: 100%;
`

const MobileNavToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  @media (min-width: ${BP_LG}) {
    display: none;
  }
`

const MobileMenuPortal = styled.div<{ $open: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 30;
  display: ${({ $open }) => ($open ? 'block' : 'none')};
`

const HamburgerIcon = (props: React.SVGAttributes<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width={24}
    height={24}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    style={{ display: 'block', flexShrink: 0 }}
    {...props}
  >
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
)

const MobileMenuDropdown = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 260px;
  max-width: calc(100vw - 32px);
  max-height: 80vh;
  overflow-y: auto;
  padding: 12px;
  background: ${MENU_NAV_INSET_BG};
  border: 2px solid ${MENU_NAV_INSET_BORDER};
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  a,
  .nav-link-inner {
    display: block;
    padding: 14px 16px;
    color: ${MENU_TEXT};
    text-decoration: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 500;
    transition: background-color 0.15s;
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: ${MENU_TEXT};
    }
  }
  .sub-item {
    margin-left: 0;
    padding: 0;
    font-size: 15px;
  }
  .mobile-item-active a {
    background-color: ${MENU_ACCENT} !important;
    color: ${MENU_TEXT};
  }
  .sub-item a,
  .sub-item span[role='button'] {
    display: block;
    padding: 10px 16px 10px 16px;
    margin-left: 12px;
    border-radius: 10px;
    font-size: 15px;
    color: ${MENU_TEXT} !important;
    transition: background-color 0.15s;
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: ${MENU_TEXT} !important;
    }
  }
  .sub-item-active a,
  .sub-item-active span[role='button'] {
    background-color: ${MENU_ACCENT} !important;
    color: ${MENU_TEXT} !important;
  }
  .mobile-submenu-label {
    color: ${MENU_TEXT} !important;
    font-size: 15px;
    padding: 8px 16px;
  }
`

function isExternal(href: string) {
  return href.startsWith('http') || href.startsWith('//')
}

function NavLink({
  entry,
  isActive,
  onNavigate,
  topLevelHrefs = [],
  showParentDot = false,
  childDotByHref = {},
}: {
  entry: {
    label: string
    href?: string
    target?: string
    items?: Array<{ label: string; href: string; target?: string; action?: string }>
  }
  isActive: boolean
  onNavigate?: () => void
  topLevelHrefs?: string[]
  showParentDot?: boolean
  childDotByHref?: Record<string, boolean>
}) {
  const { account } = useWeb3React()
  const location = useLocation()
  const { login, logout } = useAuth()
  const { onPresentConnectModal } = useWalletModal(login, logout, account)
  const { toastSuccess, toastError } = useToast()
  const [isOpen, setIsOpen] = React.useState(false)
  const [faucetLoadingHref, setFaucetLoadingHref] = React.useState<string | null>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  const handleFaucetClick = React.useCallback(
    async (e: React.MouseEvent, href: string) => {
      e.preventDefault()
      e.stopPropagation()
      if (!account) {
        onPresentConnectModal()
        toastError('Wallet not connected', 'Please connect your wallet to use the faucet.')
        return
      }
      setFaucetLoadingHref(href)
      try {
        await postFaucet(account, href)
        toastSuccess('Faucet', 'Faucet request successful.')
        setIsOpen(false)
        onNavigate?.()
      } catch (err) {
        toastError('Faucet failed', err instanceof Error ? err.message : 'Please try again later.')
      } finally {
        setFaucetLoadingHref(null)
      }
    },
    [account, onPresentConnectModal, toastError, toastSuccess, onNavigate],
  )

  React.useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  React.useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && event.target instanceof Node && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isOpen])

  if (entry.items?.length) {
    const pathMatchesChild = entry.items.some((item) => item.href && location.pathname === item.href)
    const pathMatchesTopLevel = topLevelHrefs.some((href) => href && location.pathname === href)
    const isParentActive = pathMatchesChild && !pathMatchesTopLevel
    return (
      <ClickDropdownWrap ref={dropdownRef}>
        <NavLinkItem isActive={isActive || isParentActive}>
          <span
            className="nav-link-inner"
            onClick={() => setIsOpen((prev) => !prev)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                setIsOpen((prev) => !prev)
              }
            }}
            role="button"
            tabIndex={0}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            style={{ cursor: 'pointer' }}
          >
            <LabelWithDot>{entry.label}</LabelWithDot>
            <span style={{ marginLeft: 4, fontSize: '10px', lineHeight: 1 }}>▼</span>
            {showParentDot && <ClaimableDot />}
          </span>
        </NavLinkItem>
        {isOpen && (
          <ClickDropdownMenu>
            <Flex flexDirection="column" py="8px">
              {entry.items.map((item) => {
                const external = isExternal(item.href)
                const isFaucet = item.href.startsWith('/faucet/')
                const linkContent = (
                  <Text fontSize="14px">
                    <LabelWithDot>
                      {item.label}
                      {childDotByHref[item.href] && <ClaimableDot />}
                    </LabelWithDot>
                  </Text>
                )
                return (
                  <div key={item.href} className="sub-item" style={{ padding: '6px 12px' }}>
                    {external ? (
                      <a
                        href={item.href}
                        target={item.target ?? '_blank'}
                        rel="noopener noreferrer"
                        onClick={onNavigate}
                      >
                        {linkContent}
                      </a>
                    ) : isFaucet ? (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleFaucetClick(e, item.href)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleFaucetClick(e as unknown as React.MouseEvent, item.href)
                          }
                        }}
                        style={{
                          cursor: faucetLoadingHref === item.href ? 'wait' : 'pointer',
                          opacity: faucetLoadingHref === item.href ? 0.7 : 1,
                          display: 'block',
                        }}
                      >
                        {linkContent}
                      </span>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => {
                          setIsOpen(false)
                          onNavigate?.()
                        }}
                      >
                        {linkContent}
                      </Link>
                    )}
                  </div>
                )
              })}
            </Flex>
          </ClickDropdownMenu>
        )}
      </ClickDropdownWrap>
    )
  }

  if (entry.href) {
    const external = isExternal(entry.href)
    if (external) {
      return (
        <NavLinkItem isActive={false}>
          <a href={entry.href} target={entry.target ?? '_blank'} rel="noopener noreferrer" className="nav-link-inner">
            {entry.label}
          </a>
        </NavLinkItem>
      )
    } else if (entry.href.startsWith('/faucet/')) {
      const loading = faucetLoadingHref === entry.href
      return (
        <NavLinkItem isActive={isActive}>
          <span
            role="button"
            tabIndex={0}
            className="nav-link-inner"
            onClick={(e) => handleFaucetClick(e, entry.href)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleFaucetClick(e as unknown as React.MouseEvent, entry.href)
              }
            }}
            style={{
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
              pointerEvents: loading ? 'none' : undefined,
            }}
          >
            {entry.label}
          </span>
        </NavLinkItem>
      )
    }
    return (
      <NavLinkItem isActive={isActive}>
        <Link to={entry.href} className="nav-link-inner" onClick={onNavigate}>
          {entry.label}
        </Link>
      </NavLinkItem>
    )
  }

  return null
}

const Menu = (props) => {
  const { account } = useWeb3React()
  const { login, logout } = useAuth()
  const { isDark } = useTheme()
  // const bowPriceUsd = usePriceBowUsd()
  const { t } = useTranslation()
  const [show] = usePhishingBanner()
  const location = useLocation()
  const { isXl } = useMatchBreakpoints()
  const isMobile = isXl === false
  const isMounted = useIsMounted()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const mobileMenuRef = React.useRef<HTMLDivElement>(null)
  const { toastSuccess, toastError } = useToast()
  const [faucetLoadingHref, setFaucetLoadingHref] = React.useState<string | null>(null)
  const { children } = props
  const banner = SHOW_PHISHING_BANNER && show ? <PhishingWarningBanner /> : undefined
  const links = config(t)
  const { isClaimable } = useDailyCheckinClaimable()
  const { onPresentConnectModal, onPresentAccountModal } = useWalletModal(login, logout, account)

  const handleFaucetClick = React.useCallback(
    async (e: React.MouseEvent, href: string) => {
      e.preventDefault()
      e.stopPropagation()
      if (!account) {
        onPresentConnectModal()
        toastError('Wallet not connected', 'Please connect your wallet to use the faucet.')
        return
      }
      setFaucetLoadingHref(href)
      try {
        await postFaucet(account, href)
        toastSuccess('Faucet', 'Faucet request successful.')
        setMobileMenuOpen(false)
      } catch (err) {
        toastError('Faucet failed', err instanceof Error ? err.message : 'Please try again later.')
      } finally {
        setFaucetLoadingHref(null)
      }
    },
    [account, onPresentConnectModal, toastError, toastSuccess],
  )

  React.useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  React.useEffect(() => {
    if (!mobileMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (mobileMenuRef.current && e.target instanceof Node && !mobileMenuRef.current.contains(e.target)) {
        setMobileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [mobileMenuOpen])

  const closeMobileMenu = React.useCallback(() => setMobileMenuOpen(false), [])

  const topBannerHeight = isMobile ? TOP_BANNER_HEIGHT_MOBILE : TOP_BANNER_HEIGHT
  const menuHeight = isMobile ? MENU_HEIGHT_MOBILE : MENU_HEIGHT
  const totalTopMenuHeight = isMounted && banner ? menuHeight + topBannerHeight : menuHeight
  const totalTopOffset = totalTopMenuHeight + HEADER_TOP_GAP_PX

  const homeLink = links.find((l) => l.label === 'Swap')
  const logoHref = homeLink?.href ?? '/'

  return (
    <Wrapper>
      <FixedContainer showMenu height={totalTopMenuHeight}>
        <HeaderInner>
          {banner && <TopBannerContainer height={topBannerHeight}>{banner}</TopBannerContainer>}
          <StyledNav>
            <NavFirstRow>
              <LogoLink href={logoHref} aria-label="Home">
                <img src={isDark ? '/Logo.svg' : '/Logo.svg'} alt="Logo" />
              </LogoLink>

              <NavLinksWrapper>
                <NavLinks>
                  {links.map((entry) => {
                    const isActive =
                      entry.href === '/swap'
                        ? isHomeActivePath(location.pathname)
                        : !!entry.href &&
                          (location.pathname === entry.href ||
                            (typeof entry.href === 'string' &&
                              !entry.href.startsWith('http') &&
                              location.pathname.startsWith(`${entry.href}/`)))
                    const topLevelHrefs = links.filter((e) => e.href).map((e) => e.href as string)
                    return (
                      <NavLink
                        key={entry.label}
                        entry={entry}
                        isActive={isActive}
                        topLevelHrefs={topLevelHrefs}
                        showParentDot={entry.label === t('Rewards') && isClaimable}
                        childDotByHref={entry.label === t('Rewards') ? { '/points-center': isClaimable } : {}}
                      />
                    )
                  })}
                </NavLinks>
              </NavLinksWrapper>

              {/* Mobile: Telegram, X, Hamburger on the right */}
              <NavIconsRow>
                <SocialIconLink href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Telegram">
                  <img src="/images/tg_icon.png" style={{ width: '18px', height: '18px' }} alt="Telegram" />
                </SocialIconLink>
                <SocialIconLink href={MEDIUM_URL} target="_blank" rel="noopener noreferrer" aria-label="Medium">
                  <img src="/images/x_icon.png" style={{ width: '18px', height: '18px' }} alt="Medium" />
                </SocialIconLink>
                <MobileNavToggle ref={mobileMenuRef}>
                  <Button
                    variant="text"
                    scale="sm"
                    style={{ minWidth: 'auto' }}
                    onClick={() => setMobileMenuOpen((prev) => !prev)}
                    aria-expanded={mobileMenuOpen}
                    aria-haspopup="menu"
                  >
                    <HamburgerIcon aria-hidden />
                  </Button>
                  <MobileMenuPortal $open={mobileMenuOpen}>
                    <MobileMenuDropdown>
                      {links.map((entry) => {
                        const isEntryActive =
                          entry.href === '/swap'
                            ? isHomeActivePath(location.pathname)
                            : !!entry.href &&
                              !entry.href.startsWith('http') &&
                              (location.pathname === entry.href || location.pathname.startsWith(`${entry.href}/`))
                        if (entry.items?.length) {
                          return (
                            <React.Fragment key={entry.label}>
                              <span className="mobile-submenu-label" style={{ color: MENU_TEXT }}>
                                {entry.label}
                              </span>
                              {entry.items.map((item) => {
                                const external = isExternal(item.href)
                                const isFaucet = item.href.startsWith('/faucet/')
                                const isSubItemActive = !external && !isFaucet && location.pathname === item.href
                                const loading = isFaucet && faucetLoadingHref === item.href
                                return (
                                  <div
                                    key={item.href}
                                    className={`sub-item${isSubItemActive ? ' sub-item-active' : ''}`}
                                  >
                                    {external ? (
                                      <a
                                        href={item.href}
                                        target={item.target ?? '_blank'}
                                        rel="noopener noreferrer"
                                        onClick={closeMobileMenu}
                                      >
                                        {item.label}
                                      </a>
                                    ) : isFaucet ? (
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => handleFaucetClick(e, item.href)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            handleFaucetClick(e as unknown as React.MouseEvent, item.href)
                                          }
                                        }}
                                        style={{
                                          cursor: loading ? 'wait' : 'pointer',
                                          opacity: loading ? 0.7 : 1,
                                          display: 'block',
                                          color: MENU_TEXT,
                                        }}
                                      >
                                        {item.label}
                                      </span>
                                    ) : (
                                      <Link to={item.href} onClick={closeMobileMenu}>
                                        {item.label}
                                      </Link>
                                    )}
                                  </div>
                                )
                              })}
                            </React.Fragment>
                          )
                        }
                        if (entry.href) {
                          const external = isExternal(entry.href)
                          return (
                            <div
                              key={entry.label}
                              className={isEntryActive ? 'mobile-item mobile-item-active' : 'mobile-item'}
                            >
                              {external ? (
                                <a
                                  href={entry.href}
                                  target={entry.target ?? '_blank'}
                                  rel="noopener noreferrer"
                                  onClick={closeMobileMenu}
                                >
                                  {entry.label}
                                </a>
                              ) : (
                                <Link to={entry.href} onClick={closeMobileMenu}>
                                  {entry.label}
                                </Link>
                              )}
                            </div>
                          )
                        }
                        return null
                      })}
                    </MobileMenuDropdown>
                  </MobileMenuPortal>
                </MobileNavToggle>
              </NavIconsRow>

              {/* Desktop: Telegram, X, Connect (no hamburger) */}
              <RightSection>
                <SocialIconLink href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Telegram">
                  <img src="/images/tg_icon.png" style={{ width: '18px', height: '18px' }} alt="Telegram" />
                </SocialIconLink>
                <SocialIconLink href={MEDIUM_URL} target="_blank" rel="noopener noreferrer" aria-label="Medium">
                  <img src="/images/x_icon.png" style={{ width: '18px', height: '18px' }} alt="Medium" />
                </SocialIconLink>
                {account ? (
                  <StyledButton scale="sm" onClick={onPresentAccountModal}>
                    <Text fontSize="12px" color={MENU_TEXT} fontWeight={600}>
                      {account.slice(0, 4)}...{account.slice(-4)}
                    </Text>
                  </StyledButton>
                ) : (
                  <StyledButton scale="sm" onClick={onPresentConnectModal}>
                    <Text fontSize="12px" color={MENU_TEXT} fontWeight={600}>
                      Connect Wallet
                    </Text>
                  </StyledButton>
                )}
              </RightSection>
            </NavFirstRow>

            {/* Mobile only: Connect button full width on second row */}
            <ConnectRow>
              {account ? (
                <StyledButton scale="sm" onClick={onPresentAccountModal} style={{ width: '100%' }}>
                  <Text fontSize="12px" color={MENU_TEXT} fontWeight={600}>
                    {account.slice(0, 4)}...{account.slice(-4)}
                  </Text>
                </StyledButton>
              ) : (
                <StyledButton scale="sm" onClick={onPresentConnectModal} style={{ width: '100%' }}>
                  <Text fontSize="12px" color={MENU_TEXT} fontWeight={600}>
                    Connect
                  </Text>
                </StyledButton>
              )}
            </ConnectRow>
          </StyledNav>
        </HeaderInner>
      </FixedContainer>

      <BodyWrapper>
        <Inner topOffset={totalTopOffset}>{children}</Inner>
      </BodyWrapper>
    </Wrapper>
  )
}

export default Menu
